import { markdownToPreview } from '@components/designer/utils/markdownToPreview';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import {
  LLMUsageCheckResultStatus,
  URL_REMOVAL_FLAG,
} from '@conversed/rest/src/contracts/llm-usages/check-results';
import type { TReportIssueParams } from '@conversed/rest/src/contracts/llm-usages/report-issue';
import ErrorCodes from '@conversed/shared/src/errors/error-codes';
import { toastErrorTranslated, toastInfoTranslated } from '@helpers/toast';
import { updateCheckResultFieldInState } from '@helpers/trainAIHelper';
import { useLLMUsagesActions } from '@hooks/llmUsages/useLLMUsagesActions';
import { useModal } from '@hooks/modal/useModal';
import { useTemplateResponseActions } from '@hooks/settings/template-response/useTemplateResponseActions';
import { useTemplateResponseInit } from '@hooks/settings/template-response/useTemplateResponseInit';
import { useTemplateResponses } from '@hooks/settings/template-response/useTemplateResponses';
import { useHotTopicActions } from '@hooks/train-ai/useHotTopicActions';
import { usePlatformFeatures } from '@hooks/usePlatformFeatures';
import clsx from 'clsx';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnswerEditorHandle } from '../AnswerEditor';
import { ConversationReviewActionButtons } from './ConversationReviewActionButtons';
import { ConversationReviewAdditionalActionsDrawer } from './ConversationReviewAdditionalActionsDrawer';
import { ConversationReviewAnswerSection } from './ConversationReviewAnswerSection';
import { ConversationReviewCardHeader } from './ConversationReviewCardHeader';
import { ConversationReviewCorrectionEditor } from './ConversationReviewCorrectionEditor';
import { ConversationReviewFooter } from './ConversationReviewFooter';
import { ConversationReviewQuestionSection } from './ConversationReviewQuestionSection';
import { ConversationReviewReoptimizePrompt } from './ConversationReviewReoptimizePrompt';
import { ConversationReviewSourceUrlSection } from './ConversationReviewSourceUrlSection';
import { ConversationReviewStatsHeader } from './ConversationReviewStatsHeader';
import { ConversationReviewSubheader } from './ConversationReviewSubheader';
import { ConversationReviewThreadDialog } from './ConversationReviewThreadDialog';
import { ConversationReviewUnsavedChangesPrompt } from './ConversationReviewUnsavedChangesPrompt';
import { isAIPreApprovedConversation } from './conversation-review.helpers';
import type { TConversationReviewModalProps } from './conversation-review-modal.type';
import { useBulkReview } from './hooks/useBulkReview';
import { useConversationDraftState } from './hooks/useConversationDraftState';
import { useConversationTranslation } from './hooks/useConversationTranslation';
import { useReviewValidation } from './hooks/useReviewValidation';

export const ConversationReviewModal: React.FC<
  TConversationReviewModalProps
> = ({
  data,
  topic,
  requestedHotTopicId,
  allTopics,
  isPreApprovedPreview,
  timeWindow,
}) => {
  const { t } = useTranslation();
  const { hasMultilingualTrainAI } = usePlatformFeatures();
  const { hideModal } = useModal();
  const { updateCheckResult, translateConversation } = useLLMUsagesActions();
  const {
    handleViewRelatedConversations,
    handleReview,
    handleReassignConversationHotTopic,
  } = useHotTopicActions({ timeWindow });
  const { createTemplateResponse } = useTemplateResponseActions();
  useTemplateResponseInit(true, true);
  const { templateResponses } = useTemplateResponses();

  const answerEditorRef = useRef<AnswerEditorHandle>(null);

  const {
    correctionErrors,
    clearCorrectionErrors,
    clearAnswerError,
    clearUrlError,
    validateNeedsImprovement,
  } = useReviewValidation();

  const {
    conversations,
    setConversations,
    currentIndex,
    setCurrentIndex,
    totalConversations,
    currentConversation,
    draftStates,
    currentDraft,
    setCurrentDraft,
    hasUnsavedChanges,
    discardCurrentDraft,
    removeCurrentConversation,
    clearPendingHotTopic,
  } = useConversationDraftState({
    initialData: data,
    answerEditorRef,
    // Reset validation when the user navigates to another conversation so a red ring
    // on the previous card does not carry forward.
    onConversationIndexChange: clearCorrectionErrors,
  });

  const {
    translationState,
    showTranslated,
    isTranslating,
    handleTranslate,
    translateButtonLabel,
    isCurrentConversationDutch,
    isShowingOriginalLanguage,
    displayedQuestion,
    displayedAnswer,
  } = useConversationTranslation({
    currentConversation,
    currentCorrectedAnswer: currentDraft.correctedAnswer,
    hasMultilingualTrainAI,
    answerEditorRef,
    translateConversation,
    setCurrentDraft,
  });

  // A card already handled from another context needs an explicit confirmation before its
  // first edit. Holds the status the user picked while the prompt is open, and the set of
  // cards already confirmed so the prompt is not raised twice for the same one.
  //
  // Keyed by conversation _id, not by index: applyPendingReassignment removes a card from the
  // list mid-session (and useConversationDraftState shifts the draft entries down to match),
  // so an index recorded here would afterwards point at a different conversation and silently
  // skip its confirmation.
  const [pendingReoptimizationStatus, setPendingReoptimizationStatus] =
    useState<LLMUsageCheckResultStatus | null>(null);
  const [reoptimizationConfirmedIds, setReoptimizationConfirmedIds] = useState<
    Set<string>
  >(() => new Set());

  // Derived from `conversations` (which updateCheckResultFieldInState maintains) rather than
  // counted per save, so the pre-approved preview starts at its full count and a re-optimized
  // card is never counted twice. Same rule Core uses for preApprovedCount: a direct verdict,
  // handled from another context, or an intent redirect row.
  const preApprovedCount = useMemo(
    () =>
      conversations.results.filter(
        (conversation) =>
          conversation.checkResult?.status ||
          conversation.isEvaluable === false ||
          conversation.isIntentRedirect,
      ).length,
    [conversations.results],
  );
  // Bump this on save to clear the template-response dropdown selection
  const [clearTrigger, setClearTrigger] = useState(0);

  // Tracks a pending Previous/Next navigation when the user has unsaved changes.
  // When set, shows the unsaved-changes prompt instead of navigating immediately.
  const [pendingNavigation, setPendingNavigation] = useState<
    'previous' | 'next' | null
  >(null);
  const [isSavingFromPrompt, setIsSavingFromPrompt] = useState(false);
  const [threadConversationId, setThreadConversationId] = useState<
    string | null
  >(null);
  const closeThreadDialog = useCallback(() => {
    setThreadConversationId(null);
  }, []);

  const { isBulkProcessing, handleBulkApproveAll, handleBulkMarkAllWrong } =
    useBulkReview({
      conversations,
      setConversations,
      updateCheckResult,
      requestedHotTopicId,
      onComplete: hideModal,
    });

  const getInitialCorrectedAnswer = useCallback((): string => {
    const draft = draftStates[currentIndex];
    if (draft?.correctedAnswer?.trim()) {
      return draft.correctedAnswer;
    }
    const existingCorrection =
      currentConversation?.checkResult?.correctedAnswer?.trim();
    if (existingCorrection) {
      return existingCorrection;
    }
    // Default to the currently displayed answer text
    if (showTranslated && translationState) {
      return translationState.translatedAnswer;
    }
    return currentConversation?.answer || '';
  }, [
    draftStates,
    currentIndex,
    currentConversation,
    showTranslated,
    translationState,
  ]);

  const applyStatusChange = useCallback(
    (status: LLMUsageCheckResultStatus) => {
      // Status side-effects:
      // - GOOD: auto-enable useForAIKnowledgeData (positive answers feed the dataset)
      // - IRRELEVANT: disable useForAIKnowledgeData AND uncheck isPriorityKnowledge
      //   (irrelevant answers must not feed the dataset, so they cannot be prioritized)
      // - NEEDS_IMPROVEMENT: seed the correction editor with the initial answer + URL,
      //   and auto-enable useForAIKnowledgeData too (corrected answers feed the dataset)
      if (status === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT) {
        const initialAnswer = getInitialCorrectedAnswer();
        setCurrentDraft((previousDraft) => ({
          ...previousDraft,
          status,
          correctedAnswer: initialAnswer,
          correctedURL:
            previousDraft.correctedURL ||
            currentConversation?.checkResult?.correctedURL?.trim() ||
            currentConversation?.url ||
            '',
          useForAIKnowledgeData: true,
        }));
        if (answerEditorRef.current) {
          answerEditorRef.current.setEditorText(initialAnswer);
        }
      } else if (status === LLMUsageCheckResultStatus.GOOD) {
        setCurrentDraft((previousDraft) => ({
          ...previousDraft,
          status,
          useForAIKnowledgeData: true,
        }));
        clearCorrectionErrors();
      } else {
        setCurrentDraft((previousDraft) => ({
          ...previousDraft,
          status,
          useForAIKnowledgeData: false,
          isPriorityKnowledge: false,
        }));
        clearCorrectionErrors();
      }
    },
    [
      getInitialCorrectedAnswer,
      setCurrentDraft,
      currentConversation,
      clearCorrectionErrors,
    ],
  );

  // Editing a question that was already handled elsewhere re-optimizes it: the verdict moves
  // off the document that currently holds it. Confirm once per card before letting that happen.
  const isCurrentConversationEvaluable =
    currentConversation?.isEvaluable !== false;
  const isCurrentConversationAIPreApproved = currentConversation
    ? isAIPreApprovedConversation(currentConversation)
    : false;
  // An Intent Check sent this question to a flow the A.I. does not answer: the card is read-only.
  const isCurrentConversationIntentRedirect =
    currentConversation?.isIntentRedirect === true;
  const currentConversationId = currentConversation?._id ?? '';
  const needsReoptimizationConfirm =
    !isCurrentConversationEvaluable &&
    !reoptimizationConfirmedIds.has(currentConversationId);

  const handleStatusChange = useCallback(
    (status: LLMUsageCheckResultStatus) => {
      if (isCurrentConversationIntentRedirect) {
        return;
      }
      if (needsReoptimizationConfirm) {
        setPendingReoptimizationStatus(status);
        return;
      }
      applyStatusChange(status);
    },
    [
      isCurrentConversationIntentRedirect,
      needsReoptimizationConfirm,
      applyStatusChange,
    ],
  );

  const handleConfirmReoptimization = useCallback(() => {
    setReoptimizationConfirmedIds((previous) =>
      new Set(previous).add(currentConversationId),
    );
    if (pendingReoptimizationStatus) {
      applyStatusChange(pendingReoptimizationStatus);
    }
    setPendingReoptimizationStatus(null);
  }, [currentConversationId, pendingReoptimizationStatus, applyStatusChange]);

  const handleCancelReoptimization = useCallback(() => {
    setPendingReoptimizationStatus(null);
  }, []);

  const handleCorrectedAnswerChange = useCallback(
    (value: string) => {
      if (correctionErrors.answer && value?.trim()?.length) {
        clearAnswerError();
      }
      const htmlValue = markdownToPreview(value);
      setCurrentDraft((previousDraft) => {
        // The editor fires onChange on blur regardless of whether the user typed.
        // Skip the update when the produced HTML matches the current draft - otherwise
        // a no-op focus + blur would seed a draft that differs from checkResult and
        // wrongly trigger the unsaved-changes prompt on the next navigation.
        if (previousDraft.correctedAnswer === htmlValue) {
          return previousDraft;
        }
        return { ...previousDraft, correctedAnswer: htmlValue };
      });
    },
    [setCurrentDraft, correctionErrors.answer, clearAnswerError],
  );

  const handleCorrectedURLChange = useCallback(
    (inputValue: string) => {
      const urlValue = inputValue.trim() || URL_REMOVAL_FLAG;
      setCurrentDraft((previousDraft) => ({
        ...previousDraft,
        correctedURL: urlValue,
      }));
      if (correctionErrors.url) {
        clearUrlError();
      }
    },
    [setCurrentDraft, correctionErrors.url, clearUrlError],
  );

  // Auto-fills the corrected answer and URL with the selected template's content
  const handleTemplateResponseSelect = useCallback(
    (_name: string, responseId: string) => {
      const selectedResponse = templateResponses.find(
        (response) => response._id.toString() === responseId,
      );
      if (!selectedResponse) {
        return;
      }
      const content = selectedResponse.content || '';
      const url = selectedResponse.url || '';
      setCurrentDraft((previousDraft) => ({
        ...previousDraft,
        correctedAnswer: content,
        correctedURL: url,
      }));
      answerEditorRef.current?.setEditorText(content);
    },
    [templateResponses, setCurrentDraft],
  );

  // Called by the AnswerEditor slash-command flow when a template is inserted - syncs URL only
  const handleTemplateInsert = useCallback(
    (url: string) => {
      setCurrentDraft((previousDraft) => ({
        ...previousDraft,
        correctedURL: url,
      }));
    },
    [setCurrentDraft],
  );

  const handleReportIssueChange = useCallback(
    (value: boolean) => {
      setCurrentDraft((previousDraft) => ({
        ...previousDraft,
        reportIssue: value,
      }));
    },
    [setCurrentDraft],
  );

  const handleReportIssueParamsChange = useCallback(
    (params: TReportIssueParams) => {
      setCurrentDraft((previousDraft) => ({
        ...previousDraft,
        reportIssueParams: params,
      }));
    },
    [setCurrentDraft],
  );

  const handleUseForAIKnowledgeDataChange = useCallback(
    (value: boolean) => {
      setCurrentDraft((previousDraft) => ({
        ...previousDraft,
        useForAIKnowledgeData: value,
        // Priority Knowledge requires the answer to feed the dataset, so uncheck it
        // whenever "Use for A.I. knowledge data" is turned off.
        ...(value ? {} : { isPriorityKnowledge: false }),
      }));
    },
    [setCurrentDraft],
  );

  const handleAddToPriorityKnowledgeChange = useCallback(
    (value: boolean) => {
      setCurrentDraft((previousDraft) => ({
        ...previousDraft,
        isPriorityKnowledge: value,
      }));
    },
    [setCurrentDraft],
  );

  const handleSaveAnswerAsTemplateChange = useCallback(
    (value: boolean) => {
      setCurrentDraft((previousDraft) => ({
        ...previousDraft,
        saveAnswerAsTemplate: value,
      }));
    },
    [setCurrentDraft],
  );

  const handleTemplateNameChange = useCallback(
    (value: string) => {
      setCurrentDraft((previousDraft) => ({
        ...previousDraft,
        templateName: value,
      }));
    },
    [setCurrentDraft],
  );

  const handleViewRelated = useCallback(async () => {
    await handleViewRelatedConversations(
      { id: topic.id, name: topic.name, percentage: 0 },
      // onBack re-opens the review modal with fresh unreviewed conversations
      () => handleReview(topic, allTopics),
    );
  }, [handleViewRelatedConversations, handleReview, topic, allTopics]);

  // Store the pending reassignment in draft state; the actual API call is deferred to
  // "Save & Continue" so the user can review the conversation before committing.
  // If the user re-selects the current topic, clear the pending flag instead - otherwise
  // hasUnsavedChanges would flag a no-op reassignment as dirty.
  const handleReassignHotTopic = useCallback(
    (newTopicId: string) => {
      if (isCurrentConversationIntentRedirect) {
        return;
      }
      setCurrentDraft((previousDraft) => {
        const currentTopicId = currentConversation?.hotTopicMetadata?.id;
        if (newTopicId === currentTopicId) {
          return { ...previousDraft, pendingHotTopicId: undefined };
        }
        return { ...previousDraft, pendingHotTopicId: newTopicId };
      });
    },
    [
      isCurrentConversationIntentRedirect,
      setCurrentDraft,
      currentConversation?.hotTopicMetadata?.id,
    ],
  );

  const saveCurrentCard = useCallback(async (): Promise<boolean> => {
    const draft = draftStates[currentIndex];
    const conversation = conversations.results[currentIndex];
    // A read-only intent redirect card has nothing to save; report success so the caller advances.
    if (!draft?.status || conversation?.isIntentRedirect) {
      return true;
    }
    const translationFields = translationState
      ? {
          translation: {
            question: translationState.translatedQuestion,
            answer: translationState.translatedAnswer,
            sourceLang: translationState.sourceLang,
          },
        }
      : undefined;

    try {
      // Run validation before any API call; throws on failure
      if (draft.status === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT) {
        validateNeedsImprovement(draft);
      }

      // Persist the answer as a new template before the main save so it's available
      // immediately afterwards.
      if (
        draft.saveAnswerAsTemplate &&
        draft.status === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT
      ) {
        if (!draft.templateName.trim()) {
          toastErrorTranslated('modals.save-as-template.name-required');
          return false;
        }
        await createTemplateResponse({
          name: draft.templateName.trim(),
          content: draft.correctedAnswer?.trim(),
          url: draft.correctedURL?.replace(URL_REMOVAL_FLAG, '')?.trim(),
        });
        // Clear the template fields on this draft so a subsequent save doesn't try
        // to recreate the same template (backend would 409; we'd silently swallow it,
        // but the user state would still misleadingly show the toggle as on).
        setCurrentDraft((previousDraft) => ({
          ...previousDraft,
          templateName: '',
          saveAnswerAsTemplate: false,
        }));
      }

      // If this conversation was already evaluated, treat the new save as a
      // reoptimization. The hot-topic-only conversation review modal never carries
      // a llmBlockId, so we pass undefined.
      const isReoptimization =
        Boolean(conversation.checkResult?.status) ||
        conversation.isEvaluable === false;

      const checkResult = await updateCheckResult(
        conversation._id,
        draft.status,
        draft.correctedAnswer || '',
        draft.correctedURL || '',
        draft.reportIssue,
        draft.reportIssue ? draft.reportIssueParams : undefined,
        draft.useForAIKnowledgeData,
        isReoptimization,
        undefined,
        requestedHotTopicId,
        draft.isPriorityKnowledge,
      );

      // Confirm to the user that the QA-team report was submitted
      if (checkResult?.reportIssue) {
        toastInfoTranslated(
          'train-ai.fact-checking.report-issue.submitted-successfully',
        );
      }

      if (checkResult) {
        updateCheckResultFieldInState(
          setConversations,
          conversation._id,
          checkResult,
          isReoptimization,
          undefined,
          undefined,
          translationFields,
        );
        // Reset the template-response dropdown selection after a successful save
        setClearTrigger((previousTrigger) => previousTrigger + 1);
        return true;
      }
      return false;
    } catch (error: unknown) {
      // Skip the toast when the backend reports a duplicate template name (the create-template
      // API already surfaces its own dialog upstream).
      const apiMessage = (
        error as { response?: { data?: { message?: string } } }
      )?.response?.data?.message;
      if (apiMessage === ErrorCodes.ALREADY_EXISTS) {
        return false;
      }
      if (error instanceof Error && error.message) {
        toastErrorTranslated(error.message);
      }
      return false;
    }
  }, [
    draftStates,
    currentIndex,
    conversations.results,
    translationState,
    updateCheckResult,
    requestedHotTopicId,
    createTemplateResponse,
    validateNeedsImprovement,
    setCurrentDraft,
    setConversations,
  ]);

  // Apply the draft's pending hot topic reassignment (if any) to the current conversation.
  // Two outcomes: the conversation is removed from the list (when reassigned to a different
  // topic than this session), or it stays with updated local metadata (when reassigned to
  // requestedHotTopicId). In both success paths, pendingHotTopicId is cleared so the dirty
  // check no longer flags it. The caller uses `wasRemoved` to decide how to navigate.
  const applyPendingReassignment = useCallback(async (): Promise<{
    wasRemoved: boolean;
  }> => {
    const draft = draftStates[currentIndex];
    const pendingHotTopicId = draft?.pendingHotTopicId;
    if (!pendingHotTopicId) {
      return { wasRemoved: false };
    }

    const conversation = conversations.results[currentIndex];

    // No-op: pending selection equals the current topic. Just clear the flag.
    if (pendingHotTopicId === conversation?.hotTopicMetadata?.id) {
      clearPendingHotTopic();
      return { wasRemoved: false };
    }

    const newTopic = allTopics.find(
      (topicItem) => topicItem.id === pendingHotTopicId,
    );
    const reassignSuccess = await handleReassignConversationHotTopic(
      conversation._id,
      pendingHotTopicId,
      newTopic?.name ?? '',
    );

    if (!reassignSuccess) {
      return { wasRemoved: false };
    }

    // Reassigned to a different topic than this review session - remove from the list
    // so the user does not see it again here. Drafts above the removed index shift down.
    if (pendingHotTopicId !== requestedHotTopicId) {
      removeCurrentConversation();
      return { wasRemoved: true };
    }

    // Reassigned to the same topic this session reviews - keep the card but update its
    // local hotTopicMetadata and clear the pending flag, so the dirty check is correct
    // and the reassign dropdown reflects the new state.
    setConversations((previous) => ({
      ...previous,
      results: previous.results.map((conversationItem, resultIndex) =>
        resultIndex === currentIndex
          ? {
              ...conversationItem,
              hotTopicMetadata: {
                id: pendingHotTopicId,
                name: newTopic?.name ?? '',
                percentage: 0,
              },
            }
          : conversationItem,
      ),
    }));

    clearPendingHotTopic();

    return { wasRemoved: false };
  }, [
    draftStates,
    currentIndex,
    conversations.results,
    allTopics,
    handleReassignConversationHotTopic,
    requestedHotTopicId,
    clearPendingHotTopic,
    removeCurrentConversation,
    setConversations,
  ]);

  const handleSaveAndContinue = useCallback(async () => {
    const wasSaved = await saveCurrentCard();
    // If save failed (validation error, API error, missing template name, etc.),
    // stay on this card so the user can fix the issue without losing their state.
    // The toast surfaced from saveCurrentCard tells them what went wrong.
    if (!wasSaved) {
      return;
    }

    const { wasRemoved } = await applyPendingReassignment();
    // When removed, currentIndex already points at the conversation that used to be next
    // (or has been stepped back if the removed card was the last one). Either way, no
    // further forward navigation is needed - the user sees the "next" conversation already.
    if (wasRemoved) {
      return;
    }

    if (currentIndex < totalConversations - 1) {
      setCurrentIndex((previousIndex) => previousIndex + 1);
    } else {
      hideModal();
    }
  }, [
    saveCurrentCard,
    applyPendingReassignment,
    currentIndex,
    totalConversations,
    hideModal,
    setCurrentIndex,
  ]);

  const handleSkip = useCallback(() => {
    if (currentIndex < totalConversations - 1) {
      setCurrentIndex((previousIndex) => previousIndex + 1);
    }
  }, [currentIndex, totalConversations, setCurrentIndex]);

  const handlePrevious = useCallback(() => {
    if (currentIndex === 0) {
      return;
    }
    if (hasUnsavedChanges) {
      setPendingNavigation('previous');
      return;
    }
    setCurrentIndex((previousIndex) => previousIndex - 1);
  }, [currentIndex, hasUnsavedChanges, setCurrentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex >= totalConversations - 1) {
      return;
    }
    if (hasUnsavedChanges) {
      setPendingNavigation('next');
      return;
    }
    setCurrentIndex((previousIndex) => previousIndex + 1);
  }, [currentIndex, totalConversations, hasUnsavedChanges, setCurrentIndex]);

  const handleCancelNavigation = useCallback(() => {
    setPendingNavigation(null);
  }, []);

  const handleDiscardAndNavigate = useCallback(() => {
    const direction = pendingNavigation;
    // Clear the draft so no changes persist after the user chose to discard
    discardCurrentDraft();
    setPendingNavigation(null);
    if (direction === 'previous' && currentIndex > 0) {
      setCurrentIndex((previousIndex) => previousIndex - 1);
    } else if (direction === 'next' && currentIndex < totalConversations - 1) {
      setCurrentIndex((previousIndex) => previousIndex + 1);
    }
  }, [
    pendingNavigation,
    currentIndex,
    totalConversations,
    discardCurrentDraft,
    setCurrentIndex,
  ]);

  const handleConfirmSaveAndNavigate = useCallback(async () => {
    const direction = pendingNavigation;
    setIsSavingFromPrompt(true);
    try {
      const wasSaved = await saveCurrentCard();
      if (!wasSaved) {
        // Close the prompt; the toast tells the user what went wrong, and the dirty
        // flags / red borders remain so they can fix the current card.
        setPendingNavigation(null);
        return;
      }

      const { wasRemoved } = await applyPendingReassignment();
      setPendingNavigation(null);

      if (wasRemoved) {
        // After removal, currentIndex points to what used to be the next card. For
        // 'previous' we still need to step back one to honor the user's intent; for
        // 'next' the removal already advanced us implicitly.
        if (direction === 'previous' && currentIndex > 0) {
          setCurrentIndex((previousIndex) => previousIndex - 1);
        }
        return;
      }

      if (direction === 'previous' && currentIndex > 0) {
        setCurrentIndex((previousIndex) => previousIndex - 1);
      } else if (
        direction === 'next' &&
        currentIndex < totalConversations - 1
      ) {
        setCurrentIndex((previousIndex) => previousIndex + 1);
      }
    } finally {
      setIsSavingFromPrompt(false);
    }
  }, [
    pendingNavigation,
    saveCurrentCard,
    applyPendingReassignment,
    currentIndex,
    totalConversations,
    setCurrentIndex,
  ]);

  const isEditResponseActive =
    currentDraft.status === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT;
  // While translating, running a bulk update, or saving from the unsaved-changes prompt,
  // disable all card actions so the user cannot save or navigate mid-operation.
  const isProcessing = isTranslating || isBulkProcessing || isSavingFromPrompt;

  const topMatchScore = useMemo(() => {
    if (currentConversation?.topMatchScore === undefined) {
      return null;
    }
    return Math.round(currentConversation.topMatchScore * 100);
  }, [currentConversation]);

  if (totalConversations === 0) {
    return (
      <form className='conversation-review-modal__empty-form'>
        <div className='conversation-review-modal__empty-body'>
          <p className='conversation-review-modal__empty-text'>
            {t('train-ai.conversation-review.no-conversations')}
          </p>
        </div>
        <div className='tailwind-modal-footer'>
          <ButtonT
            type='button'
            styling='cancel'
            size='xs'
            rounding='md'
            onClick={hideModal}>
            {t('general.buttons.cancel')}
          </ButtonT>
        </div>
      </form>
    );
  }

  return (
    <form className='conversation-review-modal'>
      <div className='conversation-review-modal__top'>
        <ConversationReviewStatsHeader
          topic={topic}
          timeWindow={timeWindow}
          topMatchScore={topMatchScore}
          translationState={translationState}
          allTopics={allTopics}
          currentConversationTopicId={
            currentDraft.pendingHotTopicId ??
            currentConversation?.hotTopicMetadata?.id
          }
          isActionDisabled={isProcessing}
          canReassignHotTopic={!isCurrentConversationIntentRedirect}
          onViewRelated={handleViewRelated}
          onReassignHotTopic={handleReassignHotTopic}
        />

        <ConversationReviewSubheader
          currentIndex={currentIndex}
          totalConversations={totalConversations}
          preApprovedCount={preApprovedCount}
        />

        <div className='conversation-review-modal__card-region'>
          <div
            className={clsx(
              'conversation-review-modal__card',
              !isCurrentConversationEvaluable &&
                'conversation-review-modal__card--already-evaluated',
              isCurrentConversationAIPreApproved &&
                'conversation-review-modal__card--ai-pre-approved',
              isCurrentConversationIntentRedirect &&
                'conversation-review-modal__card--intent-redirect',
            )}>
            <ConversationReviewCardHeader
              currentIndex={currentIndex}
              totalConversations={totalConversations}
              hasMultilingualTrainAI={
                hasMultilingualTrainAI && !isCurrentConversationDutch
              }
              isTranslating={isTranslating}
              isDisabled={isProcessing}
              isEvaluable={isCurrentConversationEvaluable}
              isAIPreApproved={isCurrentConversationAIPreApproved}
              isIntentRedirect={isCurrentConversationIntentRedirect}
              hasTranslation={translationState !== null}
              translateLabel={translateButtonLabel}
              onTranslate={handleTranslate}
              onSkip={handleSkip}
            />

            <div
              className={clsx(
                'conversation-review-modal__card-scroll',
                isCurrentConversationIntentRedirect &&
                  'conversation-review-modal__card-scroll--read-only',
              )}>
              <ConversationReviewQuestionSection
                displayedQuestion={displayedQuestion}
                translationState={translationState}
                showTranslated={showTranslated}
                isShowingOriginalLanguage={isShowingOriginalLanguage}
                hasMultilingualTrainAI={
                  hasMultilingualTrainAI &&
                  !isCurrentConversationDutch &&
                  !isCurrentConversationIntentRedirect
                }
                isDisabled={isProcessing}
                translateLabel={translateButtonLabel}
                onTranslate={handleTranslate}
                onOpenConversation={
                  currentConversation?.groupMetadata?.isConversationGroup
                    ? () => setThreadConversationId(currentConversationId)
                    : undefined
                }
              />

              {/* The card header already marks a redirect, and there is no A.I. answer to show. */}
              {!isCurrentConversationIntentRedirect && (
                <ConversationReviewAnswerSection
                  displayedAnswer={displayedAnswer}
                  translationState={translationState}
                  isShowingOriginalLanguage={isShowingOriginalLanguage}
                />
              )}

              {currentConversation?.url?.trim() && (
                <ConversationReviewSourceUrlSection
                  url={currentConversation.url}
                />
              )}

              {!isCurrentConversationIntentRedirect && (
                <ConversationReviewActionButtons
                  currentStatus={currentDraft.status}
                  isDisabled={isProcessing}
                  isEvaluable={isCurrentConversationEvaluable}
                  onStatusChange={handleStatusChange}
                />
              )}

              {isEditResponseActive && !isCurrentConversationIntentRedirect && (
                <ConversationReviewCorrectionEditor
                  ref={answerEditorRef}
                  conversationId={currentConversation?._id ?? ''}
                  correctedAnswer={currentDraft.correctedAnswer}
                  correctedURL={currentDraft.correctedURL}
                  templates={templateResponses}
                  clearTrigger={clearTrigger}
                  answerIsMandatory={correctionErrors.answer}
                  urlIsInvalid={correctionErrors.url}
                  onAnswerEditorFocus={() => {
                    if (correctionErrors.answer) {
                      clearAnswerError();
                    }
                  }}
                  onCorrectedAnswerChange={handleCorrectedAnswerChange}
                  onCorrectedURLChange={handleCorrectedURLChange}
                  onTemplateResponseSelect={handleTemplateResponseSelect}
                  onTemplateInsert={handleTemplateInsert}
                />
              )}
            </div>
          </div>

          {!isCurrentConversationIntentRedirect && (
            <ConversationReviewAdditionalActionsDrawer
              // Remount per question so the drawer collapses when navigating next/previous.
              key={currentIndex}
              questionNumber={currentIndex + 1}
              status={currentDraft.status}
              isDisabled={isProcessing}
              isPriorityKnowledge={currentDraft.isPriorityKnowledge}
              useForAIKnowledgeData={currentDraft.useForAIKnowledgeData}
              reportIssue={currentDraft.reportIssue}
              reportIssueParams={currentDraft.reportIssueParams}
              saveAnswerAsTemplate={currentDraft.saveAnswerAsTemplate}
              templateName={currentDraft.templateName}
              onIsPriorityKnowledgeChange={handleAddToPriorityKnowledgeChange}
              onUseForAIKnowledgeDataChange={handleUseForAIKnowledgeDataChange}
              onReportIssueChange={handleReportIssueChange}
              onReportIssueParamsChange={handleReportIssueParamsChange}
              onSaveAnswerAsTemplateChange={handleSaveAnswerAsTemplateChange}
              onTemplateNameChange={handleTemplateNameChange}
            />
          )}
        </div>
      </div>

      <ConversationReviewFooter
        isPreviousDisabled={currentIndex === 0}
        isNextDisabled={currentIndex === totalConversations - 1}
        // handleSaveAndContinue closes the modal on the last card rather than advancing,
        // so the button says so instead of promising a next question that does not exist.
        isLastConversation={currentIndex === totalConversations - 1}
        showBulkActions={!isPreApprovedPreview}
        isDisabled={isProcessing}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onApproveAll={handleBulkApproveAll}
        onMarkAllWrong={handleBulkMarkAllWrong}
        onSaveAndContinue={handleSaveAndContinue}
      />

      <ConversationReviewReoptimizePrompt
        isOpen={pendingReoptimizationStatus !== null}
        onConfirm={handleConfirmReoptimization}
        onCancel={handleCancelReoptimization}
      />

      <ConversationReviewUnsavedChangesPrompt
        isOpen={pendingNavigation !== null}
        isSaving={isSavingFromPrompt}
        onSave={handleConfirmSaveAndNavigate}
        onDiscard={handleDiscardAndNavigate}
        onCancel={handleCancelNavigation}
      />

      <ConversationReviewThreadDialog
        conversationId={threadConversationId}
        onClose={closeThreadDialog}
      />
    </form>
  );
};

export default ConversationReviewModal;
